import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type {
  ChangeEvent,
  CSSProperties,
  FocusEvent,
  MouseEvent,
  ReactElement,
  SyntheticEvent,
} from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  DropdownItemProps,
  DropdownProps,
  InputOnChangeData,
  PaginationProps,
} from 'semantic-ui-react';
import {
  Dropdown,
  Input,
  Pagination,
  Popup,
  Button,
  Icon,
} from 'semantic-ui-react';

import type { ReactTableExtendedState } from '@components/react-table/react-table';

import s from './table-pagination.scss';

const defaultPageSizeOptions: DropdownItemProps[] = [
  { key: 1, text: '5', value: 5 },
  { key: 2, text: '10', value: 10 },
  { key: 3, text: '25', value: 25 },
  { key: 4, text: '50', value: 50 },
  { key: 5, text: '100', value: 100 },
];

export const TablePagination = <T extends object>({
  className,
  filterClass,
  filterStr = '',
  getFilterContent,
  items = 'Items',
  noFilter = false,
  noFocus = false,
  noSizeOptions = false,
  onFilteredChange,
  onPageChange,
  onPageSizeChange,
  page: propsPage = 0,
  pageProps,
  pages,
  pageSize = 10,
  rawData = [],
  sizeOptions = defaultPageSizeOptions,
  state = {} as ReactTableExtendedState<T>,
  style,
}: TablePaginationProps<T>) => {
  useStyles(s);

  const [filterOpen, setFilterOpen] = useState(false);

  const getSafePage = useCallback(
    (maybeNumber: number | typeof NaN = propsPage): number => {
      const pagesCount = pages;
      const newPage = Number.isNaN(maybeNumber) ? propsPage : maybeNumber;

      return newPage > pagesCount - 1 || newPage < 0 ? 0 : newPage;
    },
    [pages, propsPage],
  );

  const page = useMemo(getSafePage, [getSafePage]);

  useEffect(() => {
    if (page !== propsPage) {
      onPageChange(page);
    }
  }, [page, propsPage, onPageChange]);

  const handlePageSizeChange = (
    _: SyntheticEvent<HTMLElement>,
    { value }: DropdownProps,
  ) => {
    if (pageSize !== value) {
      onPageSizeChange(value as number);
    }
  };

  const handlePaginationChange = (
    _: MouseEvent<HTMLAnchorElement>,
    { activePage }: PaginationProps,
  ) => {
    // @ts-expect-error -- Allow dodgy arithmetic, getSafePage handles NaN 👵
    const newPage = getSafePage(activePage - 1);

    if (propsPage !== newPage) {
      onPageChange(newPage);
    }
  };

  const handleFilterChange = (
    _: ChangeEvent<HTMLInputElement> | null,
    { value }: InputOnChangeData,
  ) => {
    if (onFilteredChange && filterStr !== value) {
      onFilteredChange('filter', value);
    }
  };

  const handleFilterFocus = (e: FocusEvent<HTMLInputElement>) => {
    // Reset value to move cursor to end of input
    const tempVal = e.target.value;
    e.target.value = '';
    e.target.value = tempVal;
  };

  const handleFilterOpen = () => {
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  return (
    <div className={classNames(className, s.root)} style={style}>
      <div className={classNames(s.pageSize, noSizeOptions ? s.hide : '')}>
        Display&nbsp;
        <Dropdown
          aria-label="Page size"
          placeholder={String(pageSize)}
          options={sizeOptions}
          value={pageSize}
          onChange={handlePageSizeChange}
          selection
          fluid
          style={{ width: 'unset', minWidth: '5rem' }}
        />
        &nbsp;
        <div style={{ width: '100%' }}>{items}</div>
      </div>
      <div
        className={classNames(s.filter, noFilter ? s.hide : '', filterClass)}
      >
        Search:&nbsp;
        <Input
          value={filterStr}
          onChange={handleFilterChange}
          onFocus={handleFilterFocus}
          autoFocus={!noFocus}
          className={getFilterContent ? s.noRightBorder : ''}
          action
          icon
        >
          <input />
          {filterStr ? (
            <Icon
              name="times"
              link
              style={{
                fontSize: '0.8rem',
                ...(getFilterContent ? { right: '2.75rem' } : {}),
              }}
              onClick={() => handleFilterChange(null, { value: '' })} // Resets filter
            />
          ) : null}
          {getFilterContent ? (
            <Popup
              // Necessary because scrolling in modals causes weird behavior
              hideOnScroll
              // Fully-controlled because sometimes hiding on scroll would not correctly bind the popup
              open={filterOpen}
              onOpen={handleFilterOpen}
              onClose={handleFilterClose}
              position="right center"
              // Ensures that on change of the sortedData, popup will recalculate position (necessary add for modals)
              popperDependencies={[rawData]}
              // Update boundary to be the viewport which allows popups to overflow within a modal
              popperModifiers={[
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
              ]}
              on="click"
              trigger={
                <Button
                  basic
                  icon="filter"
                  style={{ marginLeft: '-1px' }} // Ensures button is properly attached
                />
              }
              content={getFilterContent(state)}
            />
          ) : null}
        </Input>
      </div>
      <div className={s.pagination}>
        <Pagination
          aria-label="Pagination"
          activePage={page + 1}
          onPageChange={handlePaginationChange}
          totalPages={pages}
          firstItem={null}
          lastItem={null}
          pointing
          secondary
          {...pageProps}
        />
      </div>
    </div>
  );
};

export interface TablePaginationProps<
  T extends object,
  S = ReactTableExtendedState<T>,
> {
  className?: string;
  filterClass?: string;
  filterStr?: string;
  getFilterContent?: (state: S) => ReactElement;
  items?: string;
  noFilter?: boolean;
  noFocus?: boolean;
  noSizeOptions?: boolean;
  onFilteredChange?: (column: unknown, value: unknown) => void;
  onPageChange: (value: number) => void;
  onPageSizeChange: (value: number) => void;
  page?: number;
  pageProps?: PaginationProps;
  pages: number;
  pageSize?: number;
  rawData?: unknown[];
  sizeOptions?: DropdownItemProps[];
  state?: S;
  style?: CSSProperties;
}

export default TablePagination;
